import { useCallback } from "react";
import { sendMessage } from "utils/webview";
import { trackCapiStandardEvent } from "../analytics/metaCapi";
import { sendGoogleAdsAppEvent } from "../analytics/googleAdsAppEvent";
import { isAdTrackingSuppressed } from "../analytics/adTracking";

type Params = {
  user: any;
  isInappWebview?: boolean;
  appVersion: string;
  setMixPanel: () => void;
};

/**
 * 결제 버튼 클릭 시 공통 트래킹 훅
 * 순서: FB → Firebase/Appsflyer → CAPI → Google Ads(서버) → Mixpanel
 * - 인앱 환경에서만 FB/Firebase/Appsflyer 전송
 * - CAPI InitiateCheckout은 인앱/브라우저 공통 — 이 훅에서만 전송 (호출부 직접 전송 금지, 이중 집계 방지)
 * - Google Ads "결제 버튼 클릭 (API)"도 인앱/브라우저 공통, 누를 때마다 — 서버가 웹퍼널(Google) 유입 유저만 올린다
 */
export default function usePaymentButtonClickTracking({
  user,
  isInappWebview,
  setMixPanel,
}: Params) {
  const trackPaymentClick = useCallback(() => {
    if (!isAdTrackingSuppressed(user)) {
      if (isInappWebview) {
        // FB event
        sendMessage(
          JSON.stringify({
            event: "send_fb_event",
            params: {
              eventName: "btn_click_payment",
            },
          })
        );

        // Firebase
        sendMessage(
          JSON.stringify({
            event: "send_firebase_analytics_event",
            params: {
              eventName: "begin_checkout",
              eventParams: {
                items: [
                  {
                    item_id: "distancing_program",
                    item_name: "distancing_program",
                  },
                ],
                // GA4 잠재고객·맞춤 이벤트용 세그먼트 파라미터 (구 begin_checkout_type_a 별도 이벤트 대체)
                ...(user?.segmentType
                  ? { segment_type: user.segmentType }
                  : {}),
              },
            },
          })
        );

        // Appsflyer
        sendMessage(
          JSON.stringify({
            event: "send_appsflyer_event",
            params: {
              eventName: "af_btn_click_payment",
            },
          })
        );
      }

      // CAPI InitiateCheckout
      trackCapiStandardEvent(
        "InitiateCheckout",
        {
          externalId: user?.userId,
          email: user?.email,
        },
        undefined,
        undefined,
        isInappWebview
      );

      sendGoogleAdsAppEvent("begin_checkout");
    }

    setMixPanel();
  }, [
    user?.isInsider,
    user?.promotion,
    user?.segmentType,
    isInappWebview,
    setMixPanel,
  ]);

  return { trackPaymentClick } as const;
}
